/// <reference path="./assets.d.ts" />
import type { MascotPose } from "@orbin/core";
import browsing from "@orbin/ui-tokens/mascot/browsing.webp";
import call from "@orbin/ui-tokens/mascot/call.webp";
import decision from "@orbin/ui-tokens/mascot/decision.webp";
import done from "@orbin/ui-tokens/mascot/done.webp";
import idle from "@orbin/ui-tokens/mascot/idle.webp";
import night from "@orbin/ui-tokens/mascot/night.webp";
import team from "@orbin/ui-tokens/mascot/team.webp";
import terminal from "@orbin/ui-tokens/mascot/terminal.webp";
import type { ImgHTMLAttributes } from "react";
import { memo } from "react";
import { cn } from "./lib/utils.js";
import "./mascot.css";

const POSE_IMAGES: Record<MascotPose, string | { src: string }> = {
  idle,
  browsing,
  terminal,
  call,
  team,
  night,
  done,
  decision,
};

/** The apps import an image as its URL, and the Astro site imports it as an object that holds the URL. */
const poseSrc = (pose: MascotPose) => {
  const image = POSE_IMAGES[pose];
  return typeof image === "string" ? image : image.src;
};

/**
 * The Preá from the launch film, as a 3D render per pose. Each image is fetched only when it is
 * drawn. It breathes on a CSS transform; the motion stops for visitors who ask for reduced motion.
 */
export const Mascot = memo(function Mascot({
  pose = "idle",
  size = 160,
  motion = true,
  label,
  className,
  ...rest
}: {
  pose?: MascotPose;
  size?: number;
  motion?: boolean;
  label?: string;
} & Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "alt" | "width" | "height" | "ref">) {
  return (
    <img
      src={poseSrc(pose)}
      alt={label ?? ""}
      width={size}
      height={size}
      decoding="async"
      draggable={false}
      className={cn("prea-mascot shrink-0 select-none object-contain", className)}
      data-pose={pose}
      data-motion={motion}
      aria-hidden={label ? undefined : true}
      style={{ width: size, height: size }}
      {...rest}
    />
  );
});
